import { useMemo, useState } from "react"
import { observer } from "mobx-react-lite"
import { PublicClient } from "viem"
import { useConfig } from "wagmi"

import { Button } from "@/components/button"
import { Card } from "@/components/card"
import { ShowWhenTrue } from "@/components/conditionals"
import { InnerCard } from "@/components/inner-card"
import { InnerCardWarning } from "@/components/inner-card-warning"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { ClFarmStore } from "@/domains/cl-farm"
import { ClFarmsSubHeader } from "@/domains/cl-farms-page/components/cl-farms-sub-header"
import { DollarIcon, WalletIcon } from "@/domains/icons"
import { mintIchiClFarmLiquidityInteraction } from "@/domains/interactions"
import { dollars } from "@/domains/library"
import { TokenInputBalance } from "@/domains/tokens/components/token-input-balance"
import { TokenInputSymbol } from "@/domains/tokens/components/token-input-symbol"
import { TokenInputValue } from "@/domains/tokens/components/token-input-value"
import { ITokenPercentageButtons, TokenPercentageButtons } from "@/domains/tokens/components/token-percentage-buttons"
import { useChainConfig } from "@/hooks/use-chain-config"
import useEventEmitter from "@/hooks/useEventEmitter"

import { INSUFFICIENT_BALANCE_ERROR } from "../../../../features/liquidity/constants"
import { AlmProviderImageLink } from "../alm-provider-image-link"

/**
 * Ichi only has one token, so we only need to consider token 0
 *
 * @param param0
 * @returns
 */
export const MintIchiClFarmPair = observer(({ clFarm }: { clFarm: ClFarmStore | null | undefined }) => {
  const config = useConfig()
  const emitter = useEventEmitter()
  const chainConfig = useChainConfig()
  const scaleApp = useScaleApplication()

  const [amount, setAmount] = useState<string>("0")

  const mintToken0 = clFarm?.mintTokens?.[0]
  if (!mintToken0) {
    console.error("mintToken0 not found")
    return null
  }

  const amountBI = mintToken0?.token.parseUnits(amount ?? "0") ?? 0n
  const mintValueUsd = mintToken0?.token ? mintToken0.token.calculateValueUSD(Number(amount)) : 0

  const hasSufficientBalance = useMemo(() => {
    // we ignore potential errors if the user is not connected (nicer UI until we have an actual balance)
    if (!scaleApp.Wallet.Connection.isConnected) {
      return true
    }
    if (!amount) {
      return true
    }
    if (amountBI <= mintToken0.token.balance) {
      return true
    }
    return false
  }, [mintToken0.token.balance, amountBI, scaleApp.Wallet.Connection.isConnected])

  const canInteract = amountBI > 0n && hasSufficientBalance

  if (!clFarm) {
    return null
  }

  // #region Mint Action
  const mintAction = async () => {
    if (!scaleApp.Wallet.Connection.isConnected) {
      return emitter.emit("error-not-connected", {
        title: "Not connected",
        description: "In order to interact with this app, you first must connect your wallet.",
        variant: "error",
      })
    }
    if (!scaleApp.Wallet.Connection.connectedAddress) {
      return emitter.emit("error-not-connected", {
        title: "Not connected",
        description: "In order to interact with this app, you first must connect your wallet.",
        variant: "error",
      })
    }
    const chainId = chainConfig.chainId

    const publicClient = scaleApp.getPublicClient()

    await mintIchiClFarmLiquidityInteraction({
      amount: amountBI,
      approvalMode: scaleApp?.Settings.approvalMode,
      chainId,
      clFarm,
      config,
      connectedAddress: scaleApp.Wallet.Connection.connectedAddress.toViemAddress(),
      emitter,
      mintToken0: mintToken0.token,
      publicClient,
      onSuccess: () => {
        setAmount("0")
        // we update our CL Farm User Info
        scaleApp.ClFarms.Orchestrator.userInfoQueryInstance?.forceExecute()

        // the token should update it's balance
        scaleApp.Tokens.Orchestrator.balanceQuery?.forceExecute()
      },
    })
  }
  // #endregion

  // I'm not overly happy with this but it's good enough for now, convert to a strategy pattern later
  const buttonsToken: ITokenPercentageButtons = {
    symbol: mintToken0.token.symbol ?? "unknown",
    imageURL: mintToken0.token.imageURL ?? "",
    decimals: mintToken0.token.decimals ?? 18,
    calculateValue: (percentage: number): string => {
      return mintToken0.token.calculateValuePercentage(mintToken0.token.balance, percentage) ?? "0"
    },
  }

  return (
    <Card.Container className="flex w-full min-w-[320px] flex-col gap-6 rounded-lg border border-p400-light bg-p400 p-4 lg:p-6">
      <ShowWhenTrue when={!!clFarm}>
        <ClFarmsSubHeader displayName={clFarm?.displayName} tags={clFarm?.tags} description={clFarm?.description} />
        <InnerCard hasWarning={!hasSufficientBalance}>
          <div className="flex flex-row items-center justify-between">
            <TokenInputValue
              value={amount}
              token={mintToken0?.token}
              onValueChange={(v) => setAmount(v)}
              className="h-fit min-w-[180px] flex-grow p-0"
              placeholder="0"
            />
            <TokenInputSymbol token={mintToken0?.token} className="mr-2" />
          </div>

          {/* balances */}
          <div className="flex w-full flex-row items-center justify-between text-xs">
            <ShowWhenTrue when={hasSufficientBalance}>
              <div className="flex items-center gap-1 text-sm text-t200">
                <DollarIcon size={14} className="text-t200" />
                <p>~{dollars(mintValueUsd)}</p>
              </div>
            </ShowWhenTrue>
            <ShowWhenTrue when={!hasSufficientBalance}>
              <InnerCardWarning warning={INSUFFICIENT_BALANCE_ERROR} />
            </ShowWhenTrue>
            <Tooltip key={"balance"}>
              <TooltipTrigger asChild>
                <TokenInputBalance token={mintToken0?.token} label={<WalletIcon size={14} className="text-t200" />} />
              </TooltipTrigger>
              <TooltipContent>
                <div>{mintToken0?.token.balance.toString()}</div>
              </TooltipContent>
            </Tooltip>
          </div>

          <TokenPercentageButtons token={buttonsToken} onClick={setAmount} />
        </InnerCard>

        <div className="my-2 flex w-full items-center justify-between">
          <AlmProviderImageLink clFarm={clFarm} />
          <Button disabled={!canInteract} variant={"primary"} onClick={mintAction} size="fit">
            Mint
          </Button>
        </div>
      </ShowWhenTrue>
    </Card.Container>
  )
})
